/**
 * Супер-админка (ЛЦ-09…ЛЦ-13): общие запросы, роль в команде портала и окно «Войти в компанию».
 * Каждое действие проверяет сервер; здесь — только то, что показывать.
 */
import {
  ENTRY_DEFAULT_HOURS,
  ENTRY_HOURS,
  type EntryMode,
  type MeResponse,
  PLATFORM_ROLE_TITLES,
  type PlatformAbility,
  type PlatformRole,
  platformCan,
} from '@epc/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { type FormEvent, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { plural } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, toast } from '../../ui/feedback';
import { Checkbox, Field, Select, Textarea } from '../../ui/form';

/** Роль в команде портала у текущего человека; null — не в команде */
export function usePlatformRole(): PlatformRole | null {
  const me = useMe();
  if (me.viewAs) return null;
  return me.user.platformRole ?? (me.user.isPlatformAdmin ? 'owner' : null);
}

export function usePlatformCan(ability: PlatformAbility): boolean {
  return platformCan(usePlatformRole(), ability);
}

export function roleTitle(role: PlatformRole | null | undefined): string {
  return role ? PLATFORM_ROLE_TITLES[role] : '';
}

/** «5 сотр.», «2 внеш.» — коротко для строк списков */
export function peopleText(staff: number, external: number): string {
  const s = plural(staff, 'сотрудник', 'сотрудника', 'сотрудников');
  return external ? `${s}, ${plural(external, 'внешний', 'внешних', 'внешних')}` : s;
}

const MODES: Array<{ value: EntryMode; title: string; hint: string }> = [
  {
    value: 'view',
    title: 'Просмотр',
    hint: 'Видно всё, как администратору компании; изменения отключены',
  },
  {
    value: 'edit',
    title: 'Правка',
    hint: 'Можно менять; изменения попадут в историю компании под вашим именем',
  },
];

/**
 * Окно «Войти в компанию» (ЛЦ-11): режим, срок, причина, суммы. После входа портал перечитывает права и
 * открывает главную компании; вверху каждой страницы — плашка входа с «Выйти».
 */
export function EntryDialog({
  tenant,
  onClose,
}: {
  tenant: { id: string; name: string };
  onClose: () => void;
}) {
  const role = usePlatformRole();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<EntryMode>('view');
  const [hours, setHours] = useState<number>(ENTRY_DEFAULT_HOURS);
  const [reason, setReason] = useState('');
  const [money, setMoney] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const owner = role === 'owner';

  const enter = useMutation({
    mutationFn: () =>
      api<MeResponse>('/platform/entries', {
        body: { tenantId: tenant.id, mode, hours, reason: reason.trim() || null, money: owner && money },
      }),
    onSuccess: (next) => {
      qc.clear();
      qc.setQueryData(['me'], next);
      navigate({ to: '/' });
      toast(`Вы в компании «${tenant.name}»`);
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    if (mode === 'edit' && reason.trim().length < 5) {
      setErrors({ reason: 'Для входа с правкой напишите причину — она попадёт в журнал компании' });
      return;
    }
    enter.mutate();
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Войти в компанию «${tenant.name}»`}
      description="Вы откроете портал компании как её администратор. Сотрудником компании вы не станете."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="entry-form" loading={enter.isPending}>
            Войти
          </Button>
        </>
      }
    >
      <form id="entry-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1.5 text-[13px] font-medium text-ink">Режим</legend>
          {MODES.map((m) => (
            <label
              key={m.value}
              className={clsx(
                'flex min-h-11 cursor-pointer items-start gap-3 rounded-md border px-3 py-2.5',
                mode === m.value ? 'border-ink bg-surface-2' : 'border-line-strong',
              )}
            >
              <input
                type="radio"
                name="entry-mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => setMode(m.value)}
                className="mt-1 size-4 shrink-0 accent-[var(--color-ink)]"
              />
              <span className="flex flex-col">
                <span className="text-[14px] font-medium text-ink">{m.title}</span>
                <span className="text-[12px] leading-4 text-ink-2">{m.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <Field
          label="Сколько действует вход"
          htmlFor="entry-hours"
          hint="Потом портал сам вернёт вас в свою компанию"
        >
          <Select id="entry-hours" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {ENTRY_HOURS.map((h) => (
              <option key={h} value={h}>
                {plural(h, 'час', 'часа', 'часов')}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={mode === 'edit' ? 'Причина' : 'Причина (по желанию)'}
          htmlFor="entry-reason"
          hint="Видна в журнале компании: зачем вы входили"
          error={errors.reason}
        >
          <Textarea
            id="entry-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (errors.reason) setErrors(({ reason: _r, ...rest }) => rest);
            }}
            maxLength={500}
            placeholder="Например: обращение в поддержку — сотрудник не видит проект"
            invalid={!!errors.reason}
          />
        </Field>
        {owner ? (
          <Checkbox
            label="Показывать суммы"
            description="Бюджеты, договоры, оплаты. Администраторам портала суммы не открываются"
            checked={money}
            onChange={(e) => setMoney(e.target.checked)}
          />
        ) : (
          <p className="rounded-md bg-surface-2 px-3 py-2 text-[13px] text-ink-2">
            Суммы будут скрыты: их видит только владелец портала.
          </p>
        )}
        <p className="text-[12px] leading-4 text-ink-2">
          Компания увидит факт входа в журнале своей лицензии. В списке её сотрудников вы не появитесь, место
          по тарифу не займёте и её рассылок не получите.
        </p>
        <ErrorBox error={Object.keys(errors).length ? null : enter.error} />
      </form>
    </Dialog>
  );
}
